import { useEffect, useRef, useCallback } from 'react';
import { useMutation, useConvexAuth } from 'convex/react';
import { api } from '../../../../backend/convex/_generated/api';
import type { Id } from '../../../../backend/convex/_generated/dataModel';

const HEARTBEAT_INTERVAL_MS = 15000; // 15 seconds

export function useAssessmentSession(assignmentId: Id<"assessment_assignments"> | null) {
  const { isAuthenticated, isLoading } = useConvexAuth();
  const startSession = useMutation(api.sessions.startSession);
  const heartbeat = useMutation(api.sessions.heartbeat);
  const endSession = useMutation(api.sessions.endSession);
  
  const sessionIdRef = useRef<Id<"assessment_sessions"> | null>(null);
  const lastHeartbeatRef = useRef<number>(0);

  // Initialize session
  useEffect(() => {
    if (!assignmentId || !isAuthenticated || isLoading) return;

    const init = async () => {
      try {
        const id = await startSession({ assignmentId });
        sessionIdRef.current = id;
        lastHeartbeatRef.current = Date.now();
      } catch (err) {
        console.error('Failed to start assessment session:', err);
      }
    };

    init();
  }, [assignmentId, startSession, isAuthenticated, isLoading]);

  // Heartbeat loop
  useEffect(() => {
    if (!assignmentId) return;

    const interval = setInterval(async () => {
      if (sessionIdRef.current) {
        try {
          await heartbeat({ sessionId: sessionIdRef.current });
          lastHeartbeatRef.current = Date.now();
        } catch (err) {
          console.error('Heartbeat failed:', err);
        }
      }
    }, HEARTBEAT_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [assignmentId, heartbeat]);

  // Focus tracking
  useEffect(() => {
    if (!assignmentId) return;

    const handleBlur = () => {
      if (sessionIdRef.current) {
        heartbeat({ 
          sessionId: sessionIdRef.current,
          focusLost: true 
        }).catch(err => console.error('Focus loss report failed:', err));
      }
    };

    window.addEventListener('blur', handleBlur);
    return () => window.removeEventListener('blur', handleBlur);
  }, [assignmentId, heartbeat]);

  const updateProgress = useCallback((completedProblems: number) => {
    if (sessionIdRef.current) {
      heartbeat({
        sessionId: sessionIdRef.current,
        completedProblems
      }).catch(err => console.error('Progress update failed:', err));
    }
  }, [heartbeat]);

  const finishSession = useCallback(async () => {
    if (sessionIdRef.current) {
      await endSession({
        sessionId: sessionIdRef.current,
        status: 'completed'
      });
      sessionIdRef.current = null;
    }
  }, [endSession]);

  return {
    updateProgress,
    finishSession
  };
}

